import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SHOW_SUPPLIER_REFERRALS } from '../lib/featureFlags';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { sb } from '../supabase';
import BrandLogo from '../components/BrandLogo';
import PhoneField from '../components/PhoneField';
import {
  getSupplierOnboardingState,
  getSupplierPrimaryRoute,
} from '../lib/supplierOnboarding';
import { getIdeaFlowResumePath, hasIdeaFlowDraft } from '../lib/ideaToProductFlow';
import { buildAuthCallbackUrl, sanitizeNextPath } from '../lib/authRedirects';
import { readUtm } from '../lib/utmCapture';
import EmailCodeVerify from '../components/EmailCodeVerify';
import ForgotPassword from '../components/auth/ForgotPassword';
import { SECTIONS } from '../lib/terms';
import { track } from '../lib/growthTracker';
import { authTrigger, otpReason } from '../lib/growthEvents';

const L = {
  ar: {
    // Says WHOSE page this is, in the title. It used to read "أهلاً بك في مَعبر"
    // / "تسوّق من موردين صينيين موثوقين" — warm, and silent about the fact that
    // this is the BUYER door. A supplier who arrived here had nothing to notice.
    buyerTitle: 'دخول التجار — استورد من مصانع الصين',
    buyerSub: 'هذه صفحة التجّار (المشترين). الموردون والمصانع لهم بوابة منفصلة.',
    buyerCrossQ: 'مورّد أو مصنع؟',
    buyerCrossLink: 'سجّل من بوابة الموردين',
    supplierCrossQ: 'تاجر؟',
    supplierCrossLink: 'سجّل من هنا',
    supplierTitle: 'طلب انضمام المورد',
    supplierSub: 'هذه هي صفحة الدخول والتقديم الموحدة للموردين. إذا كان حسابك معتمداً بالفعل فسجّل الدخول مباشرة. وإذا كنت تتقدّم لأول مرة فأدخل بيانات الشركة الأساسية، أضف روابط صفحاتك التجارية، أكّد بريدك الإلكتروني، ثم سننقلك إلى لوحة المورد لإكمال التحقق.',
    email: 'البريد الإلكتروني',
    pass: 'كلمة المرور',
    firstName: 'الاسم الأول',
    lastName: 'الاسم الأخير',
    phone: 'رقم الجوال',
    city: 'المدينة',
    companyName: 'اسم الشركة / النشاط (اختياري)',
    supCompany: 'اسم الشركة',
    whatsapp: 'واتساب',
    wechat: 'WeChat',
    tradeLink: 'روابط الصفحات التجارية',
    country: 'الدولة',
    supCity: 'المدينة',
    speciality: 'التخصص',
    supplierRequiredHint: 'الحقول المعلَّمة بنجمة حمراء مطلوبة لإرسال الطلب الأساسي.',
    contactHint: 'وسائل التواصل اختيارية الآن. إذا أضفت WeChat أو WhatsApp سيسهّل ذلك على الفريق التواصل معك.',
    verificationLater: 'لن نطلب الآن السجل التجاري أو الرخصة أو صور المصنع أو بيانات استلام الأرباح. إذا احتجناها، ستكون في خطوة التحقق اللاحقة داخل حالة الحساب.',
    signin: 'تسجيل الدخول',
    signup: 'إنشاء حساب',
    toSignup: 'ما عندك حساب؟',
    toSignupLink: 'سجل الآن',
    toSignin: 'عندك حساب؟',
    toSigninLink: 'سجل دخولك',
    back: 'رجوع',
    buyerInfo: 'بياناتك',
    supInfo: 'بيانات الطلب',
    fillRequired: 'يرجى تعبئة الحقول الإجبارية.',
    termsLabel: 'أوافق على ',
    termsLink: 'الشروط والأحكام',
    mustAgreeTerms: 'يجب الموافقة على الشروط والأحكام.',
    pendingMsg: 'تم استلام طلب المورد. أرسلنا رسالة تأكيد إلى بريدك — بعد التفعيل سجّل دخولك وسنحوّلك مباشرة إلى لوحة المورد لإكمال التحقق. لن تُفتح الإجراءات الأساسية إلا بعد اكتمال التحقق ومراجعته.',
    googleLogin: 'دخول بـ Google',
    emailNotConfirmed: 'يرجى تأكيد بريدك الإلكتروني أولاً ثم متابعة طلب المورد.',
    wrongCredentials: 'إيميل أو كلمة مرور غير صحيحة.',
    requiredField: 'هذا الحقل مطلوب.',
    invalidEmail: 'تحقق من بريدك الإلكتروني — الصيغة غير صحيحة.',
    requiredContact: '',
    requiredTerms: 'يجب الموافقة على الشروط والأحكام.',
    formErrorSummary: 'فيه حقول مطلوبة ناقصة ومعلّمة باللون الأحمر.',
    cities: ['الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام', 'الخبر', 'تبوك', 'أبها', 'القصيم', 'حائل', 'جازان', 'نجران'],
  },
  en: {
    buyerTitle: 'Buyer sign in — source from verified Chinese factories',
    buyerSub: 'This is the trader (buyer) page. Suppliers and factories have their own portal.',
    buyerCrossQ: 'Supplier or factory?',
    buyerCrossLink: 'Register on the supplier portal',
    supplierCrossQ: 'Are you a trader?',
    supplierCrossLink: 'Sign in here',
    supplierTitle: 'Supplier application',
    supplierSub: 'This is the shared supplier access page for both sign-in and first-time applications. If your supplier account is already approved, sign in directly. If you are applying for the first time, submit the basic company details, add your trade page links, confirm your email, and we will take you into the supplier dashboard to complete verification.',
    email: 'Email',
    pass: 'Password',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone',
    city: 'City',
    companyName: 'Company / Business (optional)',
    supCompany: 'Company Name',
    whatsapp: 'WhatsApp',
    wechat: 'WeChat',
    tradeLink: 'Trade Page Links',
    country: 'Country',
    supCity: 'City',
    speciality: 'Specialty',
    supplierRequiredHint: 'Fields marked with a red asterisk are required for the basic supplier signup.',
    contactHint: 'Contact methods are optional at signup. Adding WeChat or WhatsApp simply helps the team reach you faster.',
    verificationLater: 'Registration number, business license, factory photos, and payout details all stay out of initial signup. If needed, they will be requested later in verification.',
    signin: 'Sign In',
    signup: 'Create Account',
    toSignup: "Don't have an account?",
    toSignupLink: 'Sign up',
    toSignin: 'Already have an account?',
    toSigninLink: 'Sign in',
    back: 'Back',
    buyerInfo: 'Your Details',
    supInfo: 'Application Details',
    fillRequired: 'Please fill the required fields.',
    termsLabel: 'I agree to ',
    termsLink: 'Terms & Conditions',
    mustAgreeTerms: 'You must agree to the Terms & Conditions.',
    pendingMsg: 'Your supplier application was received. We sent a confirmation email — after activation, sign in and we will take you straight into the supplier dashboard to complete verification. Core supplier actions unlock only after verification is submitted and approved.',
    googleLogin: 'Continue with Google',
    emailNotConfirmed: 'Please confirm your email first, then continue your supplier application.',
    wrongCredentials: 'Invalid email or password.',
    requiredField: 'This field is required.',
    invalidEmail: 'Check your email address — that format is not valid.',
    requiredContact: '',
    requiredTerms: 'You must agree to the Terms & Conditions.',
    formErrorSummary: 'Some required fields are missing and highlighted in red.',
    cities: ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Tabuk', 'Abha', 'Qassim', 'Hail', 'Jazan', 'Najran'],
  },
  zh: {
    buyerTitle: '采购商登录 — 从认证中国工厂采购',
    buyerSub: '这是采购商页面。供应商与工厂请使用单独的入口。',
    buyerCrossQ: '供应商或工厂？',
    buyerCrossLink: '请前往供应商入口注册',
    supplierCrossQ: '您是采购商？',
    supplierCrossLink: '请从这里登录',
    supplierTitle: '供应商申请',
    supplierSub: '这是供应商统一入口页，同时支持已批准账户登录和首次申请。若您的账户已经获批，请直接登录；若您是首次申请，请填写基础公司资料、补充贸易页面链接并完成邮箱确认，系统会带您进入供应商控制台继续认证。',
    email: '电子邮件',
    pass: '密码',
    firstName: '名',
    lastName: '姓',
    phone: '电话',
    city: '城市',
    companyName: '公司/商业名称（可选）',
    supCompany: '公司名称',
    whatsapp: 'WhatsApp',
    wechat: 'WeChat',
    tradeLink: '贸易页面链接',
    country: '国家',
    supCity: '城市',
    speciality: '专业领域',
    supplierRequiredHint: '带红色星号的字段为基础供应商注册所必填。',
    contactHint: '注册时联系方式为可选项。若填写 WeChat 或 WhatsApp，团队联系您会更快。',
    verificationLater: '公司注册号、营业执照、工厂照片以及收款资料都不属于首次注册内容。如有需要，会在后续认证阶段再补充。',
    signin: '登录',
    signup: '创建账户',
    toSignup: '没有账户？',
    toSignupLink: '立即注册',
    toSignin: '已有账户？',
    toSigninLink: '登录',
    back: '返回',
    buyerInfo: '您的信息',
    supInfo: '申请资料',
    fillRequired: '请填写必填项。',
    termsLabel: '我同意',
    termsLink: '条款与条件',
    mustAgreeTerms: '您必须同意条款与条件。',
    pendingMsg: '我们已收到您的供应商申请。确认邮件已发送；激活后登录，系统会直接带您进入供应商控制台继续完成认证。只有在认证提交并审核通过后，核心供应商功能才会解锁。',
    googleLogin: '使用 Google 登录',
    emailNotConfirmed: '请先确认邮箱，然后继续供应商申请。',
    wrongCredentials: '邮箱或密码错误。',
    requiredField: '此项为必填。',
    invalidEmail: '请检查邮箱地址，格式不正确。',
    requiredContact: '',
    requiredTerms: '您必须同意条款与条件。',
    formErrorSummary: '有必填项未完成，已用红色高亮显示。',
    cities: ['利雅得', '吉达', '麦加', '麦地那', '达曼', '霍拜尔', '塔布克', '艾卜哈', '盖西姆', '哈伊勒', '吉赞', '纳季兰'],
  },
};

const SUPPLIER_SIGNUP_CONTENT = {
  ar: {
    introTag: 'للموردين الصينيين',
    introTitle: 'قدّم البيانات الأساسية الآن — ثم أكمل التحقق من لوحة المورد',
    introBody: 'هذا التسجيل الأول ليس تحققاً كاملاً. الفكرة هنا أن ترسل هوية الشركة الأساسية ورابطاً تجارياً موثوقاً، وبعد تأكيد البريد تدخل مباشرة إلى لوحة المورد لإكمال التحقق.',
    steps: ['بيانات شركة أساسية', 'تأكيد البريد الإلكتروني', 'الدخول إلى لوحة المورد ثم بدء التحقق'],
    checklist: [
      'استخدم اسم الشركة الرسمي أو الاسم الذي يعرفك به المشترون',
      'أضف رابط صفحة تجارية حقيقية أو موقعاً رسمياً يمكن للفريق مراجعته بسرعة',
      'WeChat مفيد جداً إذا كان هذا هو أسلوب التواصل الأساسي لديك',
    ],
    companyHint: 'الأفضل كتابة اسم الشركة القانوني أو اسم المتجر المعروف لدى المشترين.',
    countryHint: 'إذا كانت شركتك مسجلة في الصين فاكتب الصين / China / 中国 حسب ما تفضّل.',
    cityHint: 'أدخل المدينة التي يعمل منها فريقك التجاري أو المصنع.',
    tradeLinkHint: 'أضف رابط صفحة المنتج أو صفحة الشركة أو المتجر أو الموقع الرسمي. يمكنك إدخال أكثر من رابط وفصلها بسطر جديد.',
    specialityHint: 'مثال: أدوات مطبخ، تعبئة وتغليف، إلكترونيات استهلاكية.',
    contactTitle: 'قنوات التواصل المفضلة',
    contactBody: 'WeChat اختياري لكنه يبني الثقة أكثر مع المورد الصيني لأنه القناة الأكثر توقعاً في هذه المرحلة.',
    laterTitle: 'ما سنؤجله لمرحلة لاحقة',
    laterItems: ['الرخصة التجارية الكاملة', 'صور المصنع', 'رقم التسجيل', 'بيانات استلام الأرباح'],
    citySuggestions: ['广州', '深圳', '义乌', '宁波', '佛山', '东莞', '厦门', '杭州', '苏州', '青岛'],
  },
  en: {
    introTag: 'For Chinese suppliers',
    introTitle: 'Submit the basics now — then complete verification inside the supplier dashboard',
    introBody: 'This first signup is intentionally lighter than full verification. The goal is to capture a credible company identity and trade page first, then move you into the supplier dashboard after real email confirmation so you can complete verification there.',
    steps: ['Basic company profile', 'Email confirmation', 'Open the supplier dashboard and start verification'],
    checklist: [
      'Use the legal company name or the name buyers already know',
      'Add a real trade page or company website that can be reviewed quickly',
      'WeChat is optional, but it helps if that is your normal business channel',
    ],
    companyHint: 'Best if this matches your legal company name or the storefront name buyers already know.',
    countryHint: 'If the company is registered in mainland China, enter China / 中国 if that is how you present it publicly.',
    cityHint: 'Enter the city your sales team or factory operates from.',
    tradeLinkHint: 'Add a product page, company profile, storefront, or company website. You can include more than one link by placing each one on a new line.',
    specialityHint: 'Optional — e.g. kitchenware, packaging, consumer electronics.',
    contactTitle: 'Preferred contact channels',
    contactBody: 'WeChat is optional, but for many Chinese suppliers it is the fastest trust-building contact method during review.',
    laterTitle: 'These stay for a later stage',
    laterItems: ['Full business license upload', 'Factory photo package', 'Registration number', 'Payout details'],
    citySuggestions: ['Guangzhou', 'Shenzhen', 'Yiwu', 'Ningbo', 'Foshan', 'Dongguan', 'Xiamen', 'Hangzhou', 'Suzhou', 'Qingdao'],
  },
  zh: {
    introTag: '面向中国供应商',
    introTitle: '先提交基础资料，然后在供应商控制台完成认证',
    introBody: '首次申请故意保持轻量，不是一次性做完整认证。先提交可信的公司身份和贸易资料链接，完成邮箱确认后，系统会直接带您进入供应商控制台继续认证。',
    steps: ['基础公司资料', '邮箱确认', '进入供应商控制台并开始认证'],
    checklist: [
      '请填写营业主体名称，或买家已经熟悉的公司/店铺名称',
      '请提供真实可访问的贸易页面或官网，方便快速审核',
      'WeChat 虽然不是必填，但如果这是您的常用商务渠道，建议填写',
    ],
    companyHint: '建议填写营业执照主体名称，或买家已经熟悉的店铺名称。',
    countryHint: '如果公司注册在中国大陆，可填写 China / 中国。',
    cityHint: '填写销售团队或工厂所在城市即可。',
    tradeLinkHint: '可填写产品页、公司主页、店铺页或官网。若有多个链接，可按换行逐条填写。',
    specialityHint: '可选，例如：厨房用品、包装材料、消费电子。',
    contactTitle: '优先联系渠道',
    contactBody: 'WeChat 不是必填，但对中国供应商来说，它通常是审核阶段最自然、最容易建立信任的联系方式。',
    laterTitle: '后续阶段再补充',
    laterItems: ['完整营业执照上传', '工厂照片资料', '注册号', '收款信息'],
    citySuggestions: ['广州', '深圳', '义乌', '宁波', '佛山', '东莞', '厦门', '杭州', '苏州', '青岛'],
  },
};

function trimValue(value) {
  return typeof value === 'string' ? value.trim() : value;
}


function buildFieldErrorMap({
  isSupplier,
  mode,
  signupMethod,
  values,
  agreedTerms,
  langPack,
}) {
  const errors = {};

  // Phone-identity signup (supplier OR buyer): the phone replaces email as the
  // required identifier and email becomes optional. Every other case needs email.
  const phoneSignup = mode === 'signup' && signupMethod === 'phone';
  if (phoneSignup) {
    if (!values.phoneValid) errors.supPhone = langPack.requiredField;
  } else if (!trimValue(values.email)) {
    errors.email = langPack.requiredField;
  } else if (!isDeliverableEmail(values.email)) {
    errors.email = langPack.invalidEmail;
  }
  // Optional on the phone path, but if it IS filled it still has to be sendable —
  // it becomes the account's email and every later notification goes to it.
  if (phoneSignup && trimValue(values.email) && !isDeliverableEmail(values.email)) {
    errors.email = langPack.invalidEmail;
  }
  if (!trimValue(values.pass)) errors.pass = langPack.requiredField;

  if (mode !== 'signup') return errors;

  if (isSupplier) {
    // Item 2, 2026-09-18: identifier + password + company, and nothing else.
    // country, city and speciality used to block here. They are not lost —
    // speciality is required by submit_supplier_docs on /supplier/verify, and
    // country and city are in the stage-2 payout file. They are collected
    // where they are actually needed instead of at the door, because this form
    // is the first thing a supplier arriving from a Facebook link ever sees.
    if (!trimValue(values.supCompany)) errors.supCompany = langPack.requiredField;
  } else {
    if (!trimValue(values.firstName)) errors.firstName = langPack.requiredField;
    if (!trimValue(values.lastName)) errors.lastName = langPack.requiredField;
    // When the buyer registers by phone, supPhone IS the phone — the separate
    // profile phone field is hidden, so don't require it here.
    if (!phoneSignup && !trimValue(values.phone)) errors.phone = langPack.requiredField;
    if (!trimValue(values.city)) errors.city = langPack.requiredField;
  }

  if (!agreedTerms) errors.terms = langPack.requiredTerms;

  return errors;
}


// Phone signup verifies the number instead of trusting it. The mechanism is the
// one ClaimFactory.jsx already runs in production — signInWithOtp → Send SMS Hook
// → whatsapp-send → Widers → WhatsApp — whose auth_code copy-code button was
// confirmed rendering on a real handset in en_US and zh_CN. Nothing here depends
// on auth.sms.enable_confirmations: that setting governs signUp({phone,password}),
// and this path does not use it.
//
// No code length is assumed or stated. The hosted project's otp_length is the
// dashboard's to change, supabase/config.toml has disagreed with it before, and
// pinning the input to a guessed length is what once made a real code impossible
// to type. Supabase rejects a wrong code anyway, so a loose bound costs nothing.
// Supabase accepts addresses the mail provider then refuses, and the refusal comes
// back as a bare 500 from the auth hook with nothing the applicant can act on. That
// happened on 2026-09-16 to "itsagoat.@gmail.com" — a dot immediately before the @,
// which RFC 5322 does not allow at the end of a local part and which Resend rejects
// outright. A plain /\S+@\S+\.\S+/ passes it, so the checks below are specifically
// the dot rules that shape catches and shape alone does not.
function isDeliverableEmail(raw) {
  const value = String(raw || '').trim();
  if (!value || /\s/.test(value)) return false;
  const at = value.lastIndexOf('@');
  if (at <= 0 || at === value.length - 1) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false;
  if (!domain.includes('.')) return false;
  if (/[@]/.test(local)) return false;
  return true;
}

const OTP_MIN = 4;
const OTP_MAX = 10;

const OTP_T = {
  ar: {
    title: 'أدخل رمز التحقق',
    hintWa: 'أرسلنا رمزًا إلى واتساب على الرقم',
    hintEmail: 'أرسلنا رمزًا إلى بريدك',
    sentEmail: 'أرسلنا رمز التحقق إلى بريدك الإلكتروني.',
    changeEmail: 'تغيير البريد',
    placeholder: 'الرمز',
    verify: 'تأكيد الرمز',
    resend: 'إعادة الإرسال',
    resendIn: (n) => `إعادة الإرسال بعد ${n} ثانية`,
    sent: 'أرسلنا رمز التحقق إلى واتسابك.',
    sendFailed: 'تعذّر إرسال الرمز. تأكد من الرقم أو جرّب التسجيل بالبريد.',
    sendFailedEmail: 'تعذّر إرسال الرمز إلى بريدك. تأكد من العنوان أو جرّب التسجيل برقم الجوال.',
    badCode: 'الرمز غير صحيح أو انتهت صلاحيته.',
    changeNumber: 'تغيير الرقم',
    signinWithCode: 'الدخول برمز واتساب بدل كلمة المرور',
    signinCodeHint: 'نسيت كلمة المرور أو سجّلت برقمك بدون كلمة مرور؟ نرسل لك رمزًا على واتساب.',
    noAccount: 'ما فيه حساب بهذا الرقم. أنشئ حسابًا أولًا.',
    signinVerify: 'تسجيل الدخول',
    pwTitle: 'اختر كلمة مرور',
    pwHint: 'تدخل المرة الجاية برقم جوالك وكلمة المرور، بدون رمز.',
    pwPlaceholder: 'كلمة المرور (6 أحرف أو أكثر)',
    pwSave: 'حفظ ومتابعة',
    pwShort: 'كلمة المرور لازم تكون 6 أحرف أو أكثر.',
    pwFailed: 'تعذّر حفظ كلمة المرور. حاول مرة ثانية.',
  },
  en: {
    title: 'Enter your verification code',
    hintWa: 'We sent a code on WhatsApp to',
    hintEmail: 'We sent a code to',
    sentEmail: 'We sent a verification code to your email.',
    changeEmail: 'Change email',
    placeholder: 'Code',
    verify: 'Confirm code',
    resend: 'Resend',
    resendIn: (n) => `Resend in ${n}s`,
    sent: 'We sent a verification code to your WhatsApp.',
    sendFailed: 'We could not send the code. Check the number, or sign up with email.',
    sendFailedEmail: 'We could not send the code to your email. Check the address, or sign up with your phone.',
    badCode: 'That code is wrong or has expired.',
    changeNumber: 'Change number',
    signinWithCode: 'Sign in with a WhatsApp code instead',
    signinCodeHint: 'Forgot your password, or signed up by phone without one? We will send you a code on WhatsApp.',
    noAccount: 'There is no account with this number. Create one first.',
    signinVerify: 'Sign in',
    pwTitle: 'Choose a password',
    pwHint: 'Next time, sign in with your phone number and password — no code needed.',
    pwPlaceholder: 'Password (6 characters or more)',
    pwSave: 'Save and continue',
    pwShort: 'The password must be at least 6 characters.',
    pwFailed: 'We could not save the password. Please try again.',
  },
  zh: {
    title: '请输入验证码',
    hintWa: '我们已通过 WhatsApp 将验证码发送至',
    hintEmail: '验证码已发送至',
    sentEmail: '验证码已发送至您的邮箱。',
    changeEmail: '更换邮箱',
    placeholder: '验证码',
    verify: '确认验证码',
    resend: '重新发送',
    resendIn: (n) => `${n} 秒后可重发`,
    sent: '验证码已发送至您的 WhatsApp。',
    sendFailed: '验证码发送失败。请检查号码，或改用邮箱注册。',
    sendFailedEmail: '验证码发送失败。请检查邮箱地址，或改用手机号注册。',
    badCode: '验证码错误或已过期。',
    changeNumber: '更换号码',
    signinWithCode: '改用 WhatsApp 验证码登录',
    signinCodeHint: '忘记密码，或用手机号注册时没有设置密码？我们会通过 WhatsApp 发送验证码。',
    noAccount: '该号码尚未注册账户，请先注册。',
    signinVerify: '登录',
    pwTitle: '设置密码',
    pwHint: '下次可用手机号和密码直接登录，无需验证码。',
    pwPlaceholder: '密码（至少 6 位）',
    pwSave: '保存并继续',
    pwShort: '密码至少需要 6 位。',
    pwFailed: '密码保存失败。请重试。',
  },
};

export default function Login({ user, profile, setUser, setProfile, lang }) {
  const nav = useNavigate();
  const { role: roleParam } = useParams();
  const [searchParams] = useSearchParams();
  const urlLang = searchParams.get('lang');
  const effectiveLang = (urlLang === 'en' || urlLang === 'zh') ? urlLang : lang;
  const role = roleParam === 'supplier' ? 'supplier' : 'buyer';
  const isSupplier = role === 'supplier';
  const isAr = effectiveLang === 'ar';
  const l = L[effectiveLang] || L.ar;
  const supplierSignupContent = SUPPLIER_SIGNUP_CONTENT[effectiveLang] || SUPPLIER_SIGNUP_CONTENT.en;

  // ?next= — an internal path to come back to after signing in, so a link that
  // routes through /login does not lose where the visitor was going. A factory
  // tapping a WhatsApp claim link while signed out is the case this exists for:
  // without it they land on a generic login, the conversation is gone, and they
  // do not know the platform well enough to go looking. Sanitised through the
  // same helper AuthCallback uses — internal absolute paths only, no open
  // redirect (authRedirects.js:26).
  const nextPath = useMemo(() => {
    const raw = searchParams.get('next');
    return raw ? sanitizeNextPath(raw) : '';
  }, [searchParams]);

  // useCallback so the effect below can depend on it honestly instead of
  // omitting it (this was one of the 93 baseline warnings, Login.jsx:323).
  const getInitialMode = useCallback(
    () => (searchParams.get('mode') === 'signup' ? 'signup' : 'signin'),
    [searchParams],
  );
  const [mode, setMode] = useState(getInitialMode);
  // Growth OS (step 7): which screen was opened, and from where. Fire-and-forget.
  useEffect(() => { track('auth_viewed', { mode: mode === 'signup' ? 'signup' : 'login', trigger: authTrigger(nextPath) }); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('');
  const [loading, setLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [resendMsgType, setResendMsgType] = useState('');
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // "Forgot password?" panel (email accounts only) — replaces the form while open.
  const [forgotOpen, setForgotOpen] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Identifier method: 'phone' or 'email'. Used by BOTH signup and sign-in —
  // doSignIn branches on it exactly as the signup path does.
  //
  // Phone for both roles since 2026-09-19 (owner): most Chinese suppliers have
  // no email, and a Saudi trader's number is closer to hand than their address
  // — a good share of small importers keep no working business email at all.
  // Buyers defaulted to email until then.
  //
  // At SIGN-IN we cannot know which door someone registered through, because we
  // do not know who they are until they tell us. So this is a default, not a
  // detection, and the toggle has to stay visible and usable on both views.
  const [signupMethod, setSignupMethod] = useState('phone');
  // Phone signup, step two: the code. Held here rather than on a route so a
  // refresh cannot strand a half-created account behind a URL.
  const ot = OTP_T[effectiveLang] || OTP_T.ar;
  const [otpSent, setOtpSent] = useState(false);
  const [otpChannel, setOtpChannel] = useState('phone');   // phone | email
  // Whether the code in the panel is finishing a SIGN-UP or a SIGN-IN. Sign-in
  // by WhatsApp code exists because a trader who signed up by phone in the app
  // never chose a password (the code was the sign-up), so phone + password can
  // never work for them; it also serves anyone who forgot theirs.
  const [otpPurpose, setOtpPurpose] = useState('signup');  // signup | signin
  const [otpCode, setOtpCode] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  // After a sign-in by WhatsApp code: the session, held while the trader is
  // offered a password so the next sign-in needs no code (owner, 2026-10-21:
  // a code once at sign-up, phone + password after). Most phone-only accounts
  // were made by code in the app and never had a password; this is where they
  // get one. Skippable — the code path stays as the fallback.
  const [pwAfterCode, setPwAfterCode] = useState(null);
  const [newPass, setNewPass] = useState('');

  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const id = setTimeout(() => setOtpCooldown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [otpCooldown]);
  const [supPhone, setSupPhone] = useState('');
  const [supPhoneValid, setSupPhoneValid] = useState(false);

  // Signup is a 3-step wizard (1: account, 2: details, 3: review). Account
  // creation still happens only on the final step — this state just gates which
  // group of fields renders. Sign-in ignores it entirely.
  const [step, setStep] = useState(1);

  const [supCompany, setSupCompany] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [wechat, setWechat] = useState('');
  const [tradeLink, setTradeLink] = useState('');
  // Referral code — prefilled from the ?ref= link and bound at sign-up only.
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');

  useEffect(() => {
    setMode(getInitialMode());
    setStep(1);
    setForgotOpen(false);
  }, [isSupplier, getInitialMode]);

  useEffect(() => {
    if (!user || !profile || !isSupplier || profile.role !== 'supplier') return;
    // A destination beats the default landing route: they were sent here from
    // somewhere specific. The destination guards itself (factory thread access is
    // enforced server-side), so honouring it cannot widen what they can reach.
    nav(nextPath || getSupplierPrimaryRoute(profile, user), { replace: true });
  }, [user, profile, isSupplier, nav, nextPath]);

  const hasPendingAiReview = () => {
    if (isSupplier) return false;
    return hasIdeaFlowDraft();
  };

  const validationErrors = useMemo(() => buildFieldErrorMap({
    isSupplier,
    mode,
    signupMethod,
    values: {
      email,
      pass,
      firstName,
      lastName,
      phone,
      city,
      supCompany,
      phoneValid: supPhoneValid,
    },
    agreedTerms,
    langPack: l,
  }), [
    isSupplier,
    mode,
    signupMethod,
    email,
    pass,
    firstName,
    lastName,
    phone,
    city,
    supCompany,
    supPhoneValid,
    agreedTerms,
    l,
  ]);

  const isSupplierSignup = isSupplier && mode === 'signup';
  // The phone/email toggle and phone identifier apply to supplier sign-IN too, so
  // a phone-registered supplier can authenticate; only the signup-only extras
  // (optional email, review note) stay gated on isSupplierSignup.
  // Phone-identity now applies to buyers too, not just suppliers.
  const authMode = mode === 'signup' || mode === 'signin';
  const phoneIdentity = authMode && signupMethod === 'phone';

  // ── Signup wizard: step titles + per-step validation ────────────────────────
  const wizardStepTitles = [
    isAr ? 'الحساب' : effectiveLang === 'zh' ? '账户' : 'Account',
    isSupplier
      ? (isAr ? 'بيانات الشركة' : effectiveLang === 'zh' ? '公司资料' : 'Company')
      : (isAr ? 'بياناتك' : effectiveLang === 'zh' ? '您的资料' : 'Your details'),
    isAr ? 'المراجعة' : effectiveLang === 'zh' ? '确认' : 'Review',
  ];

  const stepFieldKeys = (n) => {
    if (n === 1) return [phoneIdentity ? 'supPhone' : 'email', 'pass'];
    if (n === 2) return isSupplier
      ? []   // supplier signup: company details are optional now — completed later in onboarding
      : phoneIdentity
        ? ['firstName', 'lastName', 'city']
        : ['firstName', 'lastName', 'phone', 'city'];
    return ['terms'];
  };
  const stepHasErrors = (n) => stepFieldKeys(n).some((k) => validationErrors[k]);

  const goNextStep = () => {
    if (stepHasErrors(step)) { setShowValidation(true); return; }
    setShowValidation(false);
    setStep((s) => Math.min(3, s + 1));
  };
  const goPrevStep = () => { setShowValidation(false); setStep((s) => Math.max(1, s - 1)); };

  // Sign-in shows everything at once (the wizard is a signup-only concern).
  const showAccountStep = mode === 'signin' || (mode === 'signup' && step === 1);
  const showDetailsStep = mode === 'signup' && step === 2;
  const showReviewStep = mode === 'signup' && step === 3;

  const requiredAsterisk = <span style={{ color: '#d66b6b' }}> *</span>;

  const getEmailConfirmationRedirect = (nextPath = '/dashboard') => buildAuthCallbackUrl(nextPath, isSupplier ? 'supplier' : 'buyer');

  const resendConfirmationEmail = async (targetEmail) => {
    const normalizedEmail = trimValue(targetEmail || email);
    if (!normalizedEmail) {
      setResendMsg(l.fillRequired);
      setResendMsgType('error');
      return;
    }

    setResendingConfirmation(true);
    setResendMsg('');
    setResendMsgType('');

    const { error } = await sb.auth.resend({
      type: 'signup',
      email: normalizedEmail,
      options: {
        emailRedirectTo: getEmailConfirmationRedirect('/dashboard'),
      },
    });

    setResendingConfirmation(false);

    if (error) {
      setResendMsg(error.message);
      setResendMsgType('error');
      return;
    }

    setResendMsg(
      lang === 'ar'
        ? `أعدنا إرسال رسالة التفعيل إلى ${normalizedEmail}`
        : lang === 'zh'
          ? `确认邮件已重新发送至 ${normalizedEmail}`
          : `We resent the confirmation email to ${normalizedEmail}`
    );
    setResendMsgType('success');
  };

  // A code typed on the sign-in screen lands here. verifyOtp has already returned
  // a session by this point, so this is doSignIn's tail and nothing more: adopt the
  // user, read the profile, route. Kept beside doSignIn so the two stay in step.
  const finishEmailConfirmation = async (_session, confirmedUser) => {
    const authUser = confirmedUser || (await sb.auth.getUser()).data?.user;
    if (!authUser) return;
    setUser(authUser);
    // Deliberately NOT clearing msg/submittedEmail here: that panel is the only
    // thing on screen, and emptying it while the profile query is still in flight
    // flashes the sign-in form back for a beat. Navigation leaves the page anyway.

    const { data: profileRow } = await sb
      .from('profiles')
      .select('id,role,status,full_name,company_name,phone,city,country,speciality,wechat,whatsapp,trade_link,trade_links,reg_number,years_experience,license_photo,factory_photo')
      .eq('id', authUser.id)
      .single();

    if (profileRow) setProfile(profileRow);
    if (nextPath) { nav(nextPath, { replace: true }); return; }
    if (profileRow?.role === 'supplier') {
      nav(getSupplierPrimaryRoute(profileRow, authUser), { replace: true });
      return;
    }
    nav('/dashboard', { replace: true });
  };

  const doSignIn = async () => {
    // Phone-registered users (supplier or buyer) authenticate with phone +
    // password; everyone else with email + password.
    const usePhone = signupMethod === 'phone';
    if ((usePhone ? !supPhoneValid : !trimValue(email)) || !trimValue(pass)) {
      setShowValidation(true);
      setMsg(l.fillRequired);
      setMsgType('error');
      return;
    }
    track('auth_method_chosen', { method: usePhone ? 'phone_password' : 'email_password', mode: 'login' });

    setLoading(true);
    // Signal App's onAuthStateChange that this page is handling the login (it sets
    // user+profile and navigates itself), so the global SIGNED_IN reset doesn't
    // wipe that mid-flight and leave the user stuck on the form.
    window._passwordLoginTs = Date.now();
    const { data, error } = usePhone
      ? await sb.auth.signInWithPassword({ phone: supPhone, password: pass })
      : await sb.auth.signInWithPassword({ email, password: pass });
    setLoading(false);

    if (error) {
      const lowerMessage = error.message.toLowerCase();
      if (lowerMessage.includes('email not confirmed')) {
        setSubmittedEmail(trimValue(email));
        setMsg(l.emailNotConfirmed);
      } else if (lowerMessage.includes('invalid login credentials') || lowerMessage.includes('invalid email or password')) {
        setMsg(l.wrongCredentials);
      } else {
        setMsg(error.message);
      }
      setMsgType('error');
      return;
    }

    // Phone suppliers have no email_confirmed_at — they are confirmed via
    // phone_confirmed_at (set at signup, confirmations off). Accept either, else a
    // phone-registered supplier could never sign in.
    const identityConfirmed = data.user?.email_confirmed_at || data.user?.phone_confirmed_at;
    if (isSupplier && !usePhone && !identityConfirmed) {
      await sb.auth.signOut();
      setSubmittedEmail(trimValue(email));
      setMsg(l.emailNotConfirmed);
      setMsgType('error');
      return;
    }

    track('login_completed', { method: usePhone ? 'phone_password' : 'email_password' });
    await routeAfterSignIn(data.user);
  };

  // The tail of a successful password sign-in: adopt the user, load the profile,
  // route. Shared by doSignIn and the forgot-password flow, which ends signed in
  // with a fresh password and must land exactly where a normal sign-in would.
  const routeAfterSignIn = async (signedInUser) => {
    const authUser = signedInUser || (await sb.auth.getUser()).data?.user;
    if (!authUser) return;
    setUser(authUser);
    const { data: profile } = await sb
      .from('profiles')
      .select('id,role,status,full_name,company_name,phone,city,country,speciality,wechat,whatsapp,trade_link,trade_links,reg_number,years_experience,license_photo,factory_photo')
      .eq('id', authUser.id)
      .single();

    const nextProfile = profile;

    if (nextProfile) setProfile(nextProfile);

    // ?next= wins over every default landing route below: it is where the visitor
    // was actually going before /login interrupted them.
    if (nextPath) { nav(nextPath); return; }

    if (nextProfile?.role === 'supplier') {
      const supplierState = getSupplierOnboardingState(nextProfile, authUser);
      if (!supplierState.canAccessOperationalFeatures) {
        nav(getSupplierPrimaryRoute(nextProfile, authUser));
        return;
      }
    }

    if (hasPendingAiReview()) {
      nav(getIdeaFlowResumePath());
      return;
    }

    const draft = sessionStorage.getItem('maabar_request_draft');
    const hasDraft = draft && (() => {
      try {
        const parsed = JSON.parse(draft);
        return parsed.title_ar || parsed.title_en;
      } catch {
        return false;
      }
    })();

    nav(hasDraft ? '/factories' : '/dashboard');
  };

  // Everything that follows a verified phone: optional email, profile, factory
  // link, route in. Unchanged from the old trusted path except that it now runs
  // only after verifyOtp returned a session.
  const completePhoneSignup = async (signedInUser) => {
      try {
        if (signedInUser?.id) {
          const optionalEmail = trimValue(email);
          if (optionalEmail) {
            await sb.from('profiles').update({ email: optionalEmail }).eq('id', signedInUser.id)
              .then(() => {}, () => {});
          }
          setUser(signedInUser);
          const { data: prof } = await sb
            .from('profiles')
            .select('id,role,status,full_name,company_name,phone,city,country,speciality,wechat,whatsapp,trade_link,trade_links,reg_number,years_experience,license_photo,factory_photo')
            .eq('id', signedInUser.id)
            .single();
          if (prof) {
            setProfile(prof);
            // Auto-link this supplier to its pre-existing factory row by phone/email
            // (best-effort, server-side, no-op when nothing matches).
            if (isSupplier) { try { await sb.rpc('link_account_to_factory'); } catch { /* best-effort */ } }
            nav(nextPath || (isSupplier ? getSupplierPrimaryRoute(prof, signedInUser) : '/dashboard'));
            return;
          }
        }
      } catch (phoneFlowError) {
        console.error('[completePhoneSignup]:', phoneFlowError);
      }
    // '/dashboard' for both: DashboardRouter picks the supplier's home by role.
    // (/dashboard/supplier has no route and showed the 404 page.)
    nav(nextPath || '/dashboard');
  };

  // Phone signup, step two. The account already exists (signInWithOtp created it);
  // this exchanges the code for a session, saves the password the supplier chose so
  // password sign-in keeps working, and then completes exactly what the old trusted
  // path used to do — optional email, profile load, factory link, route in.
  const verifySignupCode = async () => {
    const token = otpCode.trim();
    if (token.length < OTP_MIN) { setMsg(ot.badCode); setMsgType('error'); return; }
    setLoading(true);
    const isEmailChannel = otpChannel === 'email';
    // An email code can be issued under either action type and they do not verify
    // interchangeably: a brand-new account gets 'signup' (GoTrue logs it as
    // user_confirmation_requested), while an account that already existed gets
    // 'magiclink', which 'email' covers. Which one you get depends on whether the
    // address was already registered — something this form cannot know — so try
    // the common one and fall back rather than guess.
    let { data, error } = await sb.auth.verifyOtp(
      isEmailChannel
        ? { email: trimValue(email), token, type: 'email' }
        : { phone: supPhone, token, type: 'sms' },
    );
    if (isEmailChannel && (error || !data?.session)) {
      const retry = await sb.auth.verifyOtp({ email: trimValue(email), token, type: 'signup' });
      if (!retry.error && retry.data?.session) { data = retry.data; error = null; }
    }
    if (error || !data?.session) {
      console.error('[verifySignupCode] verifyOtp failed:', error?.message || 'no session');
      track('otp_failed', { channel: isEmailChannel ? 'email' : 'whatsapp', mode: 'signup', reason: otpReason(error) });
      setLoading(false); setMsg(ot.badCode); setMsgType('error'); return;
    }
    track('otp_verified', { channel: isEmailChannel ? 'email' : 'whatsapp', mode: 'signup' });
    track('signup_completed', { role: isSupplier ? 'supplier' : 'buyer', method: isEmailChannel ? 'email' : 'phone' });
    // Non-fatal: they are signed in either way and can set a password from settings,
    // so a failure here must not block the account they just created.
    try {
      const { error: pwErr } = await sb.auth.updateUser({ password: pass });
      if (pwErr) console.error('[verifySignupCode] password not saved:', pwErr.message);
    } catch (e) { console.error('[verifySignupCode] password not saved:', e?.message || e); }
    setLoading(false);
    await completePhoneSignup(data.session.user);
  };

  const resendSignupCode = async () => {
    if (otpCooldown > 0) return;
    setLoading(true);
    const isEmailChannel = otpChannel === 'email';
    const { error } = await sb.auth.signInWithOtp(
      isEmailChannel
        ? { email: trimValue(email), options: { shouldCreateUser: true } }
        : { phone: supPhone, options: { shouldCreateUser: true } },
    );
    setLoading(false);
    if (error) {
      console.error('[resendSignupCode] signInWithOtp failed:', error.status || '', error.code || '', error.message || error);
      track('otp_failed', { channel: isEmailChannel ? 'email' : 'whatsapp', mode: 'signup', reason: otpReason(error) });
      setMsg(isEmailChannel ? ot.sendFailedEmail : ot.sendFailed); setMsgType('error'); return;
    }
    track('otp_requested', { channel: isEmailChannel ? 'email' : 'whatsapp', mode: 'signup' });
    setOtpCooldown(60);
    setMsg(isEmailChannel ? ot.sentEmail : ot.sent);
    setMsgType('success');
  };

  // ── Sign in by WhatsApp code ────────────────────────────────────────────────
  // Same delivery as sign-up (signInWithOtp → Send SMS Hook → WhatsApp), with
  // shouldCreateUser:false so signing in never creates an account. Prod answers
  // an unknown number with 422 otp_disabled "Signups not allowed for otp"
  // (probed 2026-09-30), shown as "no account with this number".
  const sendSigninCode = async ({ isResend = false } = {}) => {
    if (isResend && otpCooldown > 0) return;
    if (!supPhoneValid) {
      setShowValidation(true); setMsg(l.fillRequired); setMsgType('error'); return;
    }
    if (!isResend) track('auth_method_chosen', { method: 'whatsapp_code', mode: 'login' });
    setLoading(true);
    const { error } = await sb.auth.signInWithOtp({ phone: supPhone, options: { shouldCreateUser: false } });
    setLoading(false);
    if (error) {
      console.error('[sendSigninCode] signInWithOtp failed:', error.status || '', error.code || '', error.message || error);
      const m = String(error.message || '').toLowerCase();
      const noAccount = error.code === 'otp_disabled' || m.includes('signups not allowed');
      track('otp_failed', { channel: 'whatsapp', mode: 'login', reason: noAccount ? 'no_account' : otpReason(error) });
      setMsg(noAccount ? ot.noAccount : ot.sendFailed); setMsgType('error'); return;
    }
    track('otp_requested', { channel: 'whatsapp', mode: 'login' });
    setOtpChannel('phone');
    setOtpPurpose('signin');
    setOtpSent(true);
    if (!isResend) setOtpCode('');
    setOtpCooldown(60);
    setMsg(ot.sent); setMsgType('success');
  };

  const verifySigninCode = async () => {
    const token = otpCode.trim();
    if (token.length < OTP_MIN) { setMsg(ot.badCode); setMsgType('error'); return; }
    setLoading(true);
    // Same guard doSignIn sets: this page handles the login itself, so App's
    // SIGNED_IN reset must not wipe it mid-flight.
    window._passwordLoginTs = Date.now();
    const { data, error } = await sb.auth.verifyOtp({ phone: supPhone, token, type: 'sms' });
    setLoading(false);
    if (error || !data?.session) {
      console.error('[verifySigninCode] verifyOtp failed:', error?.message || 'no session');
      track('otp_failed', { channel: 'whatsapp', mode: 'login', reason: otpReason(error) });
      setMsg(ot.badCode); setMsgType('error'); return;
    }
    track('otp_verified', { channel: 'whatsapp', mode: 'login' });
    track('login_completed', { method: 'whatsapp_code' });
    setOtpSent(false); setOtpCode(''); setMsg('');
    setPwAfterCode(data.session);
  };

  const savePasswordAfterCode = async () => {
    if (newPass.length < 6) { setMsg(ot.pwShort); setMsgType('error'); return; }
    setLoading(true);
    const { error } = await sb.auth.updateUser({ password: newPass });
    setLoading(false);
    if (error) {
      console.error('[savePasswordAfterCode] updateUser failed:', error.status || '', error.code || '', error.message || error);
      setMsg(ot.pwFailed); setMsgType('error'); return;
    }
    const session = pwAfterCode;
    setPwAfterCode(null); setNewPass(''); setMsg('');
    await finishEmailConfirmation(session, session.user);
  };

  const doSignUp = async () => {
    const errors = buildFieldErrorMap({
      isSupplier,
      mode,
      signupMethod,
      values: {
        email,
        pass,
        firstName,
        lastName,
        phone,
        city,
        supCompany,
              phoneValid: supPhoneValid,
      },
      agreedTerms,
      langPack: l,
    });

    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      setMsg(l.formErrorSummary);
      setMsgType('error');
      return;
    }
    track('auth_method_chosen', { method: signupMethod === 'phone' ? 'phone' : 'email', mode: 'signup' });

    setLoading(true);

    // Registering by phone (supplier OR buyer): the phone is the auth identifier
    // and email is optional. handle_new_user copies these fields (incl. phone)
    // from the metadata, so pass the canonical E.164 through it. Keys are
    // unchanged from the email flow — only phone's value source differs.
    const usePhone = signupMethod === 'phone';

    const metaData = {
      role,
      status: isSupplier ? 'registered' : 'active',
      // Acquisition channel stashed when they opened the public RFQ link — the
      // query string is long gone by the time they reach this form. Top level
      // rather than inside the isSupplier branch because attribution is
      // attribution, and set_profile_utm reads raw_user_meta_data either way.
      // Spreads to nothing when the visit was untagged (item 8).
      ...readUtm(),
      ...(!isSupplier && {
        full_name: `${trimValue(firstName)} ${trimValue(lastName)}`.trim(),
        // Phone-method buyers have no separate phone field — supPhone IS the phone.
        phone: usePhone ? supPhone : trimValue(phone),
        city: trimValue(city),
        company_name: trimValue(companyName),
      }),
      ...(isSupplier && {
        company_name: trimValue(supCompany),
        // Only send what was actually filled. These fields left the signup form
        // in item 2; sending '' would write empty strings where the RPCs and
        // the admin list both expect null.
        ...(trimValue(whatsapp) && { whatsapp: trimValue(whatsapp) }),
        ...(trimValue(wechat) && { wechat: trimValue(wechat) }),
        ...(trimValue(tradeLink) && { trade_link: trimValue(tradeLink) }),
        lang: effectiveLang === 'zh' ? 'zh' : 'en',
        ...(usePhone && supPhone ? { phone: supPhone } : {}),
        ...(trimValue(referralCode) && { referral_code: trimValue(referralCode) }),
      }),
    };

    if (usePhone) {
      // Was: signUp({phone,password}), which returned a live session and confirmed
      // NOTHING — the number was taken on trust and no code was ever sent. Now the
      // account is created by signInWithOtp and only becomes a session once the code
      // comes back, in verifySignupCode(). The password is saved there, so signing in
      // with it afterwards still works.
      const otpOptions = { shouldCreateUser: true, data: metaData };
      const { error: otpErr } = await sb.auth.signInWithOtp({ phone: supPhone, options: otpOptions });
      setLoading(false);
      if (otpErr) {
        // Never show a raw provider string on an Arabic or Chinese page.
        console.error('[doSignUp] signInWithOtp(phone) failed:', otpErr.status || '', otpErr.code || '', otpErr.message || otpErr);
        track('otp_failed', { channel: 'whatsapp', mode: 'signup', reason: otpReason(otpErr) });
        setMsg(ot.sendFailed);
        setMsgType('error');
        return;
      }
      track('otp_requested', { channel: 'whatsapp', mode: 'signup' });
      setOtpChannel('phone');
      setOtpPurpose('signup');
      setOtpSent(true);
      setOtpCode('');
      setOtpCooldown(60);
      setMsg(ot.sent);
      setMsgType('success');
      return;
    } else {
      // Was: signUp({email,password}) with emailRedirectTo, which mailed a
      // confirmation LINK and pushed the applicant out of the page into their
      // inbox and back. Now it is the same code step the phone channel uses, on
      // this page — deliberately no emailRedirectTo, so the mail carries the
      // token rather than a round trip.
      const otpOptions = { shouldCreateUser: true, data: metaData };
      const { error: otpErr } = await sb.auth.signInWithOtp({ email: trimValue(email), options: otpOptions });
      setLoading(false);
      if (otpErr) {
        console.error('[doSignUp] signInWithOtp(email) failed:', otpErr.status || '', otpErr.code || '', otpErr.message || otpErr);
        track('otp_failed', { channel: 'email', mode: 'signup', reason: otpReason(otpErr) });
        setMsg(ot.sendFailedEmail);
        setMsgType('error');
        return;
      }
      track('otp_requested', { channel: 'email', mode: 'signup' });
      setOtpChannel('email');
      setOtpPurpose('signup');
      setOtpSent(true);
      setOtpCode('');
      setOtpCooldown(60);
      setMsg(ot.sentEmail);
      setMsgType('success');
      return;
    }
  };

  const doGoogleLogin = async () => {
    // Supplier signup/sign-in via Google: pass role=supplier so AuthCallback
    // promotes a brand-new OAuth account to supplier (claim_supplier_role) and
    // routes into the supplier app.
    // A supplier resumes on '/dashboard' (DashboardRouter routes by role);
    // /dashboard/supplier has no route (AuthCallback.jsx says so too).
    const resumePath = isSupplier
      ? '/dashboard'
      : (hasPendingAiReview() ? getIdeaFlowResumePath() : '/dashboard');
    const redirectTo = buildAuthCallbackUrl(resumePath, isSupplier ? 'supplier' : 'buyer');
    track('auth_method_chosen', { method: 'google', mode: mode === 'signup' ? 'signup' : 'login' });

    await sb.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
  };

  const inputStyle = {
    width: '100%',
    padding: '13px 15px',
    background: 'transparent',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    fontSize: 14,
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
    fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    letterSpacing: 1,
    color: 'var(--text-disabled)',
    marginBottom: 6,
    fontWeight: 500,
    textTransform: 'uppercase',
    fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
  };

  const fieldStyle = { marginBottom: 20 };

  const getFieldInputStyle = (fieldKey, extraStyle = {}) => ({
    ...inputStyle,
    ...(showValidation && validationErrors[fieldKey]
      ? {
          borderColor: '#d66b6b',
          boxShadow: '0 0 0 3px rgba(214,107,107,0.10)',
          background: 'rgba(214,107,107,0.03)',
        }
      : {}),
    ...extraStyle,
  });

  const getErrorText = (fieldKey) => (
    showValidation && validationErrors[fieldKey]
      ? <p style={{ marginTop: 7, fontSize: 12, color: '#d66b6b', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{validationErrors[fieldKey]}</p>
      : null
  );

  const helperTextStyle = {
    marginTop: 7,
    marginBottom: 0,
    fontSize: 12,
    color: 'var(--text-disabled)',
    lineHeight: 1.7,
    fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
  };

  const primaryBtnStyle = {
    width: '100%',
    background: '#1a1a1a',
    color: '#ffffff',
    border: 'none',
    padding: '14px',
    fontSize: 14,
    fontWeight: 500,
    cursor: loading ? 'not-allowed' : 'pointer',
    marginTop: 8,
    borderRadius: 'var(--radius-md)',
    transition: 'all 0.2s',
    opacity: loading ? 0.5 : 1,
    letterSpacing: 0.3,
    minHeight: 48,
    fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
  };
  const secondaryBtnStyle = {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-default)',
    padding: '14px 20px',
    fontSize: 14,
    fontWeight: 500,
    cursor: loading ? 'not-allowed' : 'pointer',
    marginTop: 8,
    borderRadius: 'var(--radius-md)',
    minHeight: 48,
    fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
  };

  const termsSections = SECTIONS[effectiveLang] || SECTIONS.ar;
  const termsDir = isAr ? 'rtl' : 'ltr';
  const termsFont = isAr ? 'var(--font-ar)' : 'var(--font-sans)';

  return (
    <>
      {/* Clarity: the sign-in page is masked whole — emails, phones, codes, passwords. */}
      <div dir={isAr ? 'rtl' : 'ltr'} data-clarity-mask="True" style={{
        minHeight: 'var(--app-dvh)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px 60px',
        background: 'var(--bg-base)',
        position: 'relative',
      }}>
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundImage: 'none',
          pointerEvents: 'none',
          zIndex: 0,
        }} />

        <button
          onClick={() => nav(-1)}
          style={{
            position: 'fixed',
            top: 72,
            right: 24,
            zIndex: 10,
            background: 'var(--bg-raised)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-tertiary)',
            padding: '7px 16px',
            borderRadius: 'var(--radius-md)',
            fontSize: 12,
            cursor: 'pointer',
            transition: 'all 0.15s',
            letterSpacing: 0.5,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--text-secondary)';
            e.currentTarget.style.borderColor = 'var(--border-muted)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--text-tertiary)';
            e.currentTarget.style.borderColor = 'var(--border-subtle)';
          }}
        >
          {isAr ? `→ ${l.back}` : `← ${l.back}`}
        </button>

        <div style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: mode === 'signup' ? (isSupplier ? 520 : 480) : 420,
          background: 'var(--bg-raised)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 20,
          padding: '32px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 10px 30px rgba(0,0,0,0.05)',
        }}>
          <div style={{ marginBottom: 40 }}>
            <BrandLogo size="sm" align={isAr ? 'flex-end' : 'flex-start'} muted />
          </div>

          <h1 style={{
            fontSize: isAr ? 28 : 32,
            fontWeight: 300,
            color: 'var(--text-primary)',
            marginBottom: 8,
            lineHeight: 1.2,
            letterSpacing: isAr ? 0 : -0.5,
            fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
          }}>
            {isSupplier ? l.supplierTitle : l.buyerTitle}
          </h1>
          <p style={{
            fontSize: 14,
            color: 'var(--text-disabled)',
            marginBottom: 40,
            fontWeight: 300,
            lineHeight: 1.7,
            fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
          }}>
            {isSupplier ? l.supplierSub : l.buyerSub}
          </p>

          {SHOW_SUPPLIER_REFERRALS && isSupplier && (
            <div style={{
              background: 'linear-gradient(135deg, #FCF8F0, #F7EDD6)',
              border: '1px solid rgba(176,141,46,.5)',
              borderRadius: 14, padding: '16px 18px', marginBottom: 32, textAlign: 'center',
            }}>
              <p style={{ margin: 0, fontSize: isAr ? 21 : 23, fontWeight: 700, color: '#8A6D1E', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {isAr ? '🎁 سجّل واربح ‏$60' : effectiveLang === 'zh' ? '🎁 注册即赚 $60' : '🎁 Sign up and earn $60'}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {isAr ? 'عن كل مورد تدعوه إلى معبر' : effectiveLang === 'zh' ? '每邀请一位供应商加入 Maabar' : 'for every supplier you invite to Maabar'}
              </p>
            </div>
          )}

          {msg && (
            <div style={{
              fontSize: 13,
              marginBottom: 20,
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              ...(msgType === 'error'
                ? { background: 'rgba(138,58,58,0.12)', color: '#c98a8a', border: '1px solid rgba(214,107,107,0.24)' }
                : { background: 'rgba(58,122,82,0.12)', color: '#7bc091', border: '1px solid rgba(58,122,82,0.2)' }),
              fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              lineHeight: 1.7,
            }}>
              {msg}
            </div>
          )}

          {/* Once a code is out, it is the ONLY thing on screen. It used to render
              inside the form, which the success state replaces — so the send
              succeeded and the input the applicant needed was gone. Giving it its
              own branch also answers the reason this flow exists: nobody should be
              hunting for where to type the code. */}
          {pwAfterCode ? (
              <div data-set-password="1" style={{ ...fieldStyle, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {ot.pwTitle}
                </p>
                <p style={{ margin: '6px 0 12px', fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {ot.pwHint}
                </p>
                <input
                  type="password"
                  style={inputStyle}
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  placeholder={ot.pwPlaceholder}
                  aria-label={ot.pwTitle}
                  autoComplete="new-password"
                  dir="ltr"
                />
                <button
                  onClick={savePasswordAfterCode}
                  disabled={loading || newPass.length < 6}
                  style={{ ...primaryBtnStyle, width: '100%', marginTop: 14, opacity: (loading || newPass.length < 6) ? 0.5 : 1 }}
                >
                  {loading ? '...' : ot.pwSave}
                </button>
              </div>
          ) : otpSent ? (
              <div style={{ ...fieldStyle, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {ot.title}
                </p>
                <p style={{ margin: '6px 0 12px', fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {otpChannel === 'email' ? ot.hintEmail : ot.hintWa}{' '}
                  <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{otpChannel === 'email' ? trimValue(email) : supPhone}</span>
                </p>
                <input
                  style={{ ...inputStyle, letterSpacing: 6, textAlign: 'center', fontFamily: 'Inter, sans-serif' }}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, OTP_MAX))}
                  placeholder={ot.placeholder}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  dir="ltr"
                />
                <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={otpPurpose === 'signin' ? () => sendSigninCode({ isResend: true }) : resendSignupCode}
                    disabled={otpCooldown > 0 || loading}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: otpCooldown > 0 ? 'default' : 'pointer', fontSize: 12.5, color: otpCooldown > 0 ? 'var(--text-disabled)' : 'var(--accent)', textDecoration: otpCooldown > 0 ? 'none' : 'underline', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                  >
                    {otpCooldown > 0 ? ot.resendIn(otpCooldown) : ot.resend}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setOtpSent(false); setOtpCode(''); setMsg(''); }}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, color: 'var(--text-secondary)', textDecoration: 'underline', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                  >
                    {otpChannel === 'email' ? ot.changeEmail : ot.changeNumber}
                  </button>
                </div>
                <button
                  onClick={otpPurpose === 'signin' ? verifySigninCode : verifySignupCode}
                  disabled={loading || otpCode.trim().length < OTP_MIN}
                  style={{ ...primaryBtnStyle, width: '100%', marginTop: 14, opacity: (loading || otpCode.trim().length < OTP_MIN) ? 0.5 : 1 }}
                >
                  {loading ? '...' : (otpPurpose === 'signin' ? ot.signinVerify : ot.verify)}
                </button>
              </div>
          /* NOT gated on isSupplier, and that gate is what this replaces.
             `isSupplier` is read from the route — /login/:role — so anyone who
             arrives at bare /login saw the red "confirm your email first" line
             and NOTHING else: no code field, no resend, no way forward. That is
             how Yiwu Younique stayed locked out on 2026-09-18 after the panel
             had already been restored, and it is not a legacy problem: someone
             who signs up today, does not finish, and comes back through the
             header's sign-in button lands in exactly the same place.
             A buyer in that state was never served by this panel at all. */
          ) : submittedEmail && (msgType === 'success' || msg === l.emailNotConfirmed) ? (
            <div style={{
              marginBottom: 22,
              padding: '16px 18px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--bg-subtle)',
            }}>
              <p style={{
                margin: '0 0 6px',
                fontSize: 11,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                color: 'var(--text-disabled)',
              }}>
                {isAr ? 'بريد التفعيل' : lang === 'zh' ? '确认邮箱' : 'Confirmation email'}
              </p>
              <p style={{
                margin: 0,
                fontSize: 14,
                color: 'var(--text-primary)',
                lineHeight: 1.8,
                fontFamily: 'var(--font-sans)',
                wordBreak: 'break-word',
              }}>
                {submittedEmail}
              </p>
              <p style={{
                margin: '10px 0 0',
                fontSize: 12,
                color: 'var(--text-secondary)',
                lineHeight: 1.7,
                fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              }}>
                {/* The supplier line talks about team review, which is a
                    supplier's next step and means nothing to a trader. Now that
                    this panel serves both, each is told what confirming unlocks
                    for them. */}
                {isSupplier
                  ? (isAr
                    ? 'لن يدخل طلبك مراجعة الفريق إلا بعد تأكيد هذا البريد. إذا كان العنوان خطأ، عدّل الإيميل أولاً أو اطلب إعادة الإرسال.'
                    : lang === 'zh'
                      ? '只有在此邮箱完成确认后，团队审核才会开始。如果邮箱填错，请先修改邮箱或重新发送确认邮件。'
                      : 'Your application will not enter team review until this email is confirmed. If the address is wrong, correct it first or resend the confirmation email.')
                  : (isAr
                    ? 'لن تتمكّن من الدخول إلى حسابك قبل تأكيد هذا البريد. إذا كان العنوان خطأ، عدّل الإيميل أولاً أو اطلب إعادة الإرسال.'
                    : lang === 'zh'
                      ? '在确认此邮箱之前，您无法登录账户。如果邮箱填错，请先修改邮箱或重新发送确认邮件。'
                      : 'You cannot sign in until this email is confirmed. If the address is wrong, correct it first or resend the confirmation email.')}
              </p>

              {/* Restored. 66854f1 put this here on 2026-09-05; merge a425986 the
                  next day adopted the other side of Login.jsx wholesale and the
                  field went with it. Signup kept its own inline code card, so the
                  loss only shows on the way BACK IN: a supplier who never confirmed
                  reaches this panel, has the code in hand, and until now had
                  nowhere on the page to type it. */}
              <EmailCodeVerify
                email={submittedEmail}
                lang={lang}
                onVerified={finishEmailConfirmation}
              />

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                <button
                  onClick={() => resendConfirmationEmail(submittedEmail)}
                  disabled={resendingConfirmation}
                  style={{
                    background: '#1a1a1a',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    minHeight: 42,
                    padding: '10px 16px',
                    cursor: resendingConfirmation ? 'not-allowed' : 'pointer',
                    opacity: resendingConfirmation ? 0.5 : 1,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {resendingConfirmation
                    ? '...'
                    : (isAr ? 'إعادة إرسال التفعيل' : lang === 'zh' ? '重新发送确认邮件' : 'Resend confirmation')}
                </button>

                <button
                  onClick={() => {
                    setMsg('');
                    setMsgType('');
                    setSubmittedEmail('');
                    setResendMsg('');
                    setResendMsgType('');
                    setEmail('');
                    setPass('');
                    setMode('signup');
                  }}
                  style={{
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    minHeight: 42,
                    padding: '10px 16px',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  {isAr ? 'استخدام بريد مختلف' : lang === 'zh' ? '使用其他邮箱' : 'Use a different email'}
                </button>
              </div>

              {resendMsg ? (
                <p style={{
                  margin: '12px 0 0',
                  fontSize: 12,
                  lineHeight: 1.7,
                  color: resendMsgType === 'error' ? '#d66b6b' : '#7bc091',
                  fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                }}>
                  {resendMsg}
                </p>
              ) : null}
            </div>
          ) : null}

          {forgotOpen ? (
            <ForgotPassword
              lang={effectiveLang}
              initialEmail={email}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              helperTextStyle={helperTextStyle}
              primaryBtnStyle={primaryBtnStyle}
              onCancel={() => setForgotOpen(false)}
              onDone={async (u) => { await routeAfterSignIn(u); }}
            />
          ) : !isSupplier || msgType !== 'success' ? (
            <>
              {mode === 'signup' && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    {[1, 2, 3].map((n) => (
                      <div key={n} style={{
                        flex: 1,
                        height: 3,
                        borderRadius: 999,
                        background: n <= step ? 'var(--text-primary)' : 'var(--border-default)',
                        transition: 'background 0.2s',
                      }} />
                    ))}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {wizardStepTitles[step - 1]}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-disabled)', letterSpacing: 1 }}>
                      {isAr ? `خطوة ${step} من 3` : effectiveLang === 'zh' ? `第 ${step}/3 步` : `Step ${step} of 3`}
                    </span>
                  </div>
                </div>
              )}

              {authMode && showAccountStep && (
                <div style={{ ...fieldStyle, display: 'flex', gap: 8 }}>
                  {[
                    ['phone', isAr ? 'رقم الجوال' : effectiveLang === 'zh' ? '手机号' : 'Phone'],
                    ['email', isAr ? 'البريد الإلكتروني' : effectiveLang === 'zh' ? '邮箱' : 'Email'],
                  ].map(([m, label]) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setSignupMethod(m)}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                        border: signupMethod === m ? '1px solid var(--text-primary)' : '1px solid var(--border-default)',
                        background: signupMethod === m ? 'var(--text-primary)' : 'transparent',
                        color: signupMethod === m ? 'var(--bg-base)' : 'var(--text-secondary)',
                        fontWeight: signupMethod === m ? 700 : 400,
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {showAccountStep && (phoneIdentity ? (
                <>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>
                      {isAr ? 'رقم الجوال' : effectiveLang === 'zh' ? '手机号' : 'Phone number'}{requiredAsterisk}
                    </label>
                    <PhoneField
                      value={supPhone}
                      onChange={(e164, valid) => { setSupPhone(e164); setSupPhoneValid(valid); }}
                      // ROLE-AWARE, and this one line was the whole lockout.
                      // It was hard-coded to CN for every role and both modes.
                      //
                      // The failure is SILENT, which is why it went undiagnosed.
                      // libphonenumber does NOT reject a Saudi national number
                      // under region CN — it accepts it and normalises it to a
                      // Chinese one. Measured:
                      //     typed 0537550534, region CN -> +86537550534  (valid)
                      //     typed 0537550534, region SA -> +966537550534
                      // So a Saudi trader on the Phone tab sent +86537550534 to
                      // signInWithPassword, matched no account, and got
                      // "Invalid login credentials" — told their password was
                      // wrong when it was right, about a number they never
                      // typed. A validation error would at least have pointed
                      // at the field.
                      //
                      // Phone sign-in itself has worked since 2026-07-25
                      // (68db0c1). It was reported on 2026-09-19 as a missing
                      // feature; it was a default.
                      defaultCountry={isSupplier ? 'CN' : 'SA'}
                      lang={effectiveLang}
                      inputStyle={inputStyle}
                      selectStyle={inputStyle}
                      invalid={showValidation && !!validationErrors.supPhone}
                    />
                    {getErrorText('supPhone')}
                    {isSupplierSignup && (
                      <p style={helperTextStyle}>
                        {isAr
                          ? 'نرسل رمزًا على واتساب لتأكيد الرقم مرة وحدة، وبعدها تدخل بالرقم وكلمة المرور. تُراجَع هويتك يدويًا لاحقًا.'
                          : effectiveLang === 'zh'
                            ? '我们会通过 WhatsApp 发送一次验证码确认号码，之后用手机号和密码登录。您的身份稍后将人工审核。'
                            : 'We send one WhatsApp code to confirm the number; after that you sign in with the number and your password. Your identity is reviewed manually later.'}
                      </p>
                    )}
                  </div>
                  {mode === 'signup' && (
                    <div style={fieldStyle}>
                      <label style={labelStyle}>
                        {l.email}{' '}
                        <span style={{ textTransform: 'none', opacity: 0.6 }}>
                          {isAr ? '(اختياري)' : effectiveLang === 'zh' ? '（可选）' : '(optional)'}
                        </span>
                      </label>
                      <input
                        style={getFieldInputStyle('email')}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="example@email.com"
                        autoComplete="email"
                        dir="ltr"
                      />
                    </div>
                  )}
                </>
              ) : (
                <div style={fieldStyle}>
                  <label style={labelStyle}>{l.email}{requiredAsterisk}</label>
                  <input
                    style={getFieldInputStyle('email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    autoComplete="email"
                    dir="ltr"
                  />
                  {getErrorText('email')}
                </div>
              ))}

              {showAccountStep && (
              <div style={fieldStyle}>
                <label style={labelStyle}>{l.pass}{requiredAsterisk}</label>
                <input
                  style={getFieldInputStyle('pass')}
                  type="password"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  dir="ltr"
                />
                {getErrorText('pass')}
                {/* Email accounts only. Phone accounts have the WhatsApp-code
                    sign-in just below, which needs no password at all. */}
                {mode === 'signin' && !phoneIdentity && (
                  <button
                    type="button"
                    onClick={() => {
                      setForgotOpen(true);
                      setMsg(''); setMsgType(''); setSubmittedEmail('');
                      setResendMsg(''); setResendMsgType(''); setShowValidation(false);
                    }}
                    style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, color: 'var(--accent)', textDecoration: 'underline', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                  >
                    {isAr ? 'نسيت كلمة المرور؟' : effectiveLang === 'zh' ? '忘记密码？' : 'Forgot password?'}
                  </button>
                )}
              </div>
              )}

              {/* Replaces "Forgot your password? Contact support (no SMS reset
                  yet)". The code path needs no password at all. */}
              {phoneIdentity && mode === 'signin' && (
                <div style={{ marginTop: -6, marginBottom: 16 }}>
                  <p style={{ ...helperTextStyle, margin: '0 0 6px' }}>{ot.signinCodeHint}</p>
                  <button
                    type="button"
                    onClick={() => sendSigninCode()}
                    disabled={loading}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: 'var(--accent)', textDecoration: 'underline', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                  >
                    {ot.signinWithCode}
                  </button>
                </div>
              )}

              {!isSupplier && showDetailsStep && (
                <>
                  <div style={{ fontSize: 10, letterSpacing: 2.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 16, marginTop: 8, fontWeight: 500 }}>
                    {l.buyerInfo}
                  </div>
                  <div style={{ display: 'flex', gap: 14 }}>
                    <div style={{ ...fieldStyle, flex: 1 }}>
                      <label style={labelStyle}>{l.firstName}{requiredAsterisk}</label>
                      <input style={getFieldInputStyle('firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={isAr ? 'محمد' : lang === 'zh' ? '伟' : 'John'} />
                      {getErrorText('firstName')}
                    </div>
                    <div style={{ ...fieldStyle, flex: 1 }}>
                      <label style={labelStyle}>{l.lastName}{requiredAsterisk}</label>
                      <input style={getFieldInputStyle('lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={isAr ? 'العمري' : lang === 'zh' ? '张' : 'Smith'} />
                      {getErrorText('lastName')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 14 }}>
                    {!phoneIdentity && (
                      <div style={{ ...fieldStyle, flex: 1 }}>
                        <label style={labelStyle}>{l.phone}{requiredAsterisk}</label>
                        <input style={getFieldInputStyle('phone')} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+966 5x xxx xxxx" dir="ltr" />
                        {getErrorText('phone')}
                      </div>
                    )}
                    <div style={{ ...fieldStyle, flex: 1 }}>
                      <label style={labelStyle}>{l.city}{requiredAsterisk}</label>
                      <select style={{ ...getFieldInputStyle('city'), cursor: 'pointer' }} value={city} onChange={(e) => setCity(e.target.value)}>
                        <option value="">{isAr ? 'اختر مدينة' : lang === 'zh' ? '选择城市' : 'Select city'}</option>
                        {l.cities.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {getErrorText('city')}
                    </div>
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>{l.companyName}</label>
                    <input style={inputStyle} value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={isAr ? 'اختياري' : lang === 'zh' ? '可选' : 'Optional'} />
                  </div>
                </>
              )}

              {isSupplier && showDetailsStep && (
                <>
                  <div style={{ fontSize: 10, letterSpacing: 2.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 10, marginTop: 8, fontWeight: 500 }}>
                    {l.supInfo}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-disabled)', marginTop: 0, marginBottom: 18, lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {l.supplierRequiredHint}
                  </p>

                  <div style={fieldStyle}>
                    <label style={labelStyle}>{l.supCompany}{requiredAsterisk}</label>
                    <input style={getFieldInputStyle('supCompany')} value={supCompany} onChange={(e) => setSupCompany(e.target.value)} autoComplete="organization" />
                    {getErrorText('supCompany')}
                    <p style={helperTextStyle}>{supplierSignupContent.companyHint}</p>
                  </div>

                </>
              )}

              {isSupplier && showReviewStep && (
                <>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>{l.tradeLink}</label>
                    <textarea
                      style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
                      value={tradeLink}
                      onChange={(e) => setTradeLink(e.target.value)}
                      placeholder={effectiveLang === 'ar' ? 'https://...' : 'https://...'}
                      dir="ltr"
                      autoComplete="off"
                    />
                    <p style={helperTextStyle}>{supplierSignupContent.tradeLinkHint}</p>
                  </div>

                  {/* ?ref= still prefills referralCode and is still written to
                      pending_referral_code — only the manual entry field is hidden. */}
                  <div style={{ ...fieldStyle, display: SHOW_SUPPLIER_REFERRALS ? undefined : 'none' }}>
                    <label style={labelStyle}>
                      {isAr ? 'كود الإحالة (اختياري)' : lang === 'zh' ? '推荐码（可选）' : 'Referral code (optional)'}
                    </label>
                    <input
                      style={inputStyle}
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value)}
                      placeholder={isAr ? 'أدخل الكود' : lang === 'zh' ? '输入推荐码' : 'Enter code'}
                      dir="ltr"
                      autoComplete="off"
                    />
                    <p style={helperTextStyle}>
                      {isAr ? 'إذا وصلك كود من مورد، أدخله هنا وقت التسجيل.'
                        : lang === 'zh' ? '如果供应商与您分享了推荐码，请在注册时输入。'
                        : 'If a supplier shared a code with you, enter it here at sign-up.'}
                    </p>
                  </div>

                  <div style={{
                    marginBottom: 18,
                    padding: '14px 16px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-subtle)',
                    background: 'var(--bg-subtle)',
                  }}>
                    <p style={{ margin: '0 0 10px', fontSize: 11, letterSpacing: isAr ? 0 : 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                      {supplierSignupContent.contactTitle}
                    </p>
                    <div style={{ display: 'flex', gap: 14 }}>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>{l.wechat}</label>
                        <input style={inputStyle} value={wechat} onChange={(e) => setWechat(e.target.value)} dir="ltr" placeholder="WeChat ID" autoComplete="off" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>{l.whatsapp}</label>
                        <input style={inputStyle} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+..." dir="ltr" autoComplete="tel" />
                      </div>
                    </div>
                    <p style={{ ...helperTextStyle, marginTop: 8 }}>{l.contactHint}</p>
                  </div>
                </>
              )}

              {showReviewStep && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '12px 14px',
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${showValidation && validationErrors.terms ? 'rgba(214,107,107,0.35)' : 'var(--border-subtle)'}`,
                    background: showValidation && validationErrors.terms ? 'rgba(214,107,107,0.04)' : 'var(--bg-subtle)',
                  }}>
                    <input
                      type="checkbox"
                      id="terms-cb"
                      checked={agreedTerms}
                      onChange={(e) => setAgreedTerms(e.target.checked)}
                      style={{ marginTop: 2, accentColor: 'var(--accent)', flexShrink: 0 }}
                    />
                    <label htmlFor="terms-cb" style={{
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                      lineHeight: 1.6,
                    }}>
                      {l.termsLabel}
                      <button
                        onClick={() => setShowTerms(true)}
                        style={{ color: 'var(--accent)', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit', padding: 0 }}
                      >
                        {l.termsLink}
                      </button>
                    </label>
                  </div>
                  {getErrorText('terms')}
                </div>
              )}

              {mode === 'signin' ? (
                <button onClick={doSignIn} disabled={loading} style={primaryBtnStyle}>
                  {loading ? '...' : l.signin}
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 10 }}>
                  {step > 1 && (
                    <button type="button" onClick={goPrevStep} disabled={loading} style={secondaryBtnStyle}>
                      {isAr ? 'رجوع' : effectiveLang === 'zh' ? '上一步' : 'Back'}
                    </button>
                  )}
                  {step < 3 ? (
                    <button type="button" onClick={goNextStep} style={{ ...primaryBtnStyle, flex: 1 }}>
                      {isAr ? 'التالي' : effectiveLang === 'zh' ? '下一步' : 'Next'}
                    </button>
                  ) : (
                    <button
                      onClick={doSignUp}
                      disabled={loading}
                      style={{ ...primaryBtnStyle, flex: 1 }}
                    >
                      {loading ? '...' : (isSupplier ? (isAr ? 'إرسال طلب المورد' : lang === 'zh' ? '提交供应商申请' : 'Submit supplier application') : l.signup)}
                    </button>
                  )}
                </div>
              )}

              {/* Buyers only. Google is blocked in mainland China, which is the entire
                  supplier audience, so offering it on the supplier surface is a dead end
                  that costs a signup. doGoogleLogin and l.googleLogin stay: this is the
                  same block buyers use, gated, not removed. */}
              {showAccountStep && !isSupplier && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
                    <span style={{ fontSize: 11, color: 'var(--text-disabled)', letterSpacing: 1 }}>
                      {isAr ? 'أو' : lang === 'zh' ? '或' : 'OR'}
                    </span>
                    <div style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
                  </div>
                  <button
                    onClick={doGoogleLogin}
                    style={{
                      width: '100%',
                      background: 'var(--bg-raised)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-default)',
                      padding: '12px',
                      fontSize: 13,
                      cursor: 'pointer',
                      borderRadius: 'var(--radius-md)',
                      transition: 'all 0.2s',
                      minHeight: 44,
                      fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border-strong)';
                      e.currentTarget.style.background = 'var(--bg-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border-default)';
                      e.currentTarget.style.background = 'var(--bg-raised)';
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    {l.googleLogin}
                  </button>
                </>
              )}

              <p style={{
                textAlign: 'center',
                marginTop: 24,
                fontSize: 13,
                color: 'var(--text-disabled)',
                fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              }}>
                {isSupplier
                  ? (mode === 'signin'
                    ? (isAr ? 'ما عندك طلب بعد؟' : lang === 'zh' ? '还没有申请？' : 'No application yet?')
                    : (isAr ? 'سبق وتقدمت؟' : lang === 'zh' ? '已申请？' : 'Already applied?'))
                  : (mode === 'signin' ? l.toSignup : l.toSignin)}{' '}
                <button
                  onClick={() => {
                    setMode(mode === 'signin' ? 'signup' : 'signin');
                    setStep(1);
                    setMsg('');
                    setMsgType('');
                    setSubmittedEmail('');
                    setResendMsg('');
                    setResendMsgType('');
                    setShowValidation(false);
                  }}
                  style={{
                    color: 'var(--text-secondary)',
                    fontWeight: 500,
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    textDecoration: 'underline',
                    fontSize: 13,
                    transition: 'color 0.15s',
                    fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                >
                  {isSupplier
                    ? (mode === 'signin'
                      ? (isAr ? 'ابدأ الطلب' : lang === 'zh' ? '开始申请' : 'Start application')
                      : (isAr ? 'تسجيل الدخول' : lang === 'zh' ? '登录' : 'Sign in'))
                    : (mode === 'signin' ? l.toSignupLink : l.toSigninLink)}
                </button>
              </p>
            </>
          ) : null}

          {/* The other door. Placed OUTSIDE the conditional above so it shows in
              every mode (sign-in, signup, OTP) — a supplier who has landed on
              the buyer page needs the way out at whatever step they noticed.
              Not a hidden footnote and not a second CTA either: the page has one
              primary action, and this is a correction for people who took a
              wrong turn. No route change — /login/:role already serves both. */}
          <p style={{
            marginTop: 28,
            paddingTop: 18,
            borderTop: '1px solid var(--border-subtle)',
            fontSize: 13,
            color: 'var(--text-disabled)',
            fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
            textAlign: 'center',
          }}>
            {isSupplier ? l.supplierCrossQ : l.buyerCrossQ}{' '}
            <button
              type="button"
              onClick={() => nav(isSupplier ? '/login/buyer' : '/login/supplier')}
              style={{
                color: 'var(--text-primary)',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                textDecoration: 'underline',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
              }}
            >
              {isSupplier ? l.supplierCrossLink : l.buyerCrossLink}
            </button>
          </p>
        </div>
      </div>

      {showTerms && (
        <div dir={isAr ? 'rtl' : 'ltr'} onClick={() => setShowTerms(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.07)',
            borderRadius: 12, maxWidth: 600, width: '100%', maxHeight: '80vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: '20px 28px', borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: 14, fontWeight: 600, letterSpacing: isAr || lang === 'zh' ? 0 : 1, color: 'rgba(0,0,0,0.88)', fontFamily: termsFont, margin: 0 }}>
                {isAr ? 'الشروط والأحكام' : effectiveLang === 'zh' ? '条款与条件' : 'Terms & Conditions'}
              </p>
              <button onClick={() => setShowTerms(false)} style={{ background: 'none', border: 'none', color: 'rgba(0,0,0,0.45)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '24px 28px' }} dir={termsDir}>
              {termsSections.map((section, index) => (
                <div key={index} style={{ marginBottom: 24 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(0,0,0,0.88)', fontFamily: termsFont, marginBottom: 8 }}>{section.title}</p>
                  <p style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)', fontFamily: termsFont, lineHeight: 1.9, whiteSpace: 'pre-line', margin: 0 }}>{section.body}</p>
                </div>
              ))}
            </div>
            <div style={{ padding: '16px 28px', borderTop: '1px solid rgba(0,0,0,0.07)', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => setShowTerms(false)} style={{ background: 'var(--bg-subtle)', border: '1px solid rgba(0,0,0,0.10)', color: 'rgba(0,0,0,0.60)', padding: '9px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontFamily: termsFont }}>
                {isAr ? 'إغلاق' : effectiveLang === 'zh' ? '关闭' : 'Close'}
              </button>
              <button onClick={() => { setAgreedTerms(true); setShowTerms(false); }} style={{ background: '#1a1a1a', border: 'none', color: '#ffffff', padding: '9px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: termsFont }}>
                {isAr ? 'أوافق' : effectiveLang === 'zh' ? '我同意' : 'I agree'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}